import React, { useEffect, useRef, useState } from 'react';

// Small, dependency-free Leaflet wrapper — imported by BusinessForm & FieldVisit page.
// Uses OpenStreetMap tiles (no API key needed).

const LEAFLET_CSS_HREF = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';

function ensureLeafletCss() {
  if (typeof document === 'undefined') return;
  if (!document.querySelector(`link[data-lf]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = LEAFLET_CSS_HREF;
    link.setAttribute('data-lf', '1');
    document.head.appendChild(link);
  }
}

// A clickable map used to set/pick a lat/lng.
export function MapPicker({ latitude, longitude, onChange, height = 300 }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    ensureLeafletCss();
    let cancelled = false;
    (async () => {
      const L = (await import('leaflet')).default;
      if (cancelled || !ref.current || mapRef.current) return;
      const startLat = latitude ? Number(latitude) : 20.5937;
      const startLng = longitude ? Number(longitude) : 78.9629;
      const map = L.map(ref.current).setView([startLat, startLng], latitude ? 14 : 4);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
        maxZoom: 19,
      }).addTo(map);
      const marker = L.marker([startLat, startLng], { draggable: true }).addTo(map);
      marker.on('dragend', () => {
        const p = marker.getLatLng();
        onChange && onChange({ latitude: p.lat.toFixed(6), longitude: p.lng.toFixed(6) });
      });
      map.on('click', (e) => {
        marker.setLatLng(e.latlng);
        onChange && onChange({ latitude: e.latlng.lat.toFixed(6), longitude: e.latlng.lng.toFixed(6) });
      });
      mapRef.current = map;
      markerRef.current = marker;
    })();
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markerRef.current = null; }
    };
  // eslint-disable-next-line
  }, []);

  // Recenter marker when external state changes (e.g. Use my location)
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    if (latitude && longitude) {
      const p = [Number(latitude), Number(longitude)];
      markerRef.current.setLatLng(p);
      mapRef.current.setView(p, Math.max(mapRef.current.getZoom(), 14));
    }
  }, [latitude, longitude]);

  return <div ref={ref} style={{ width: '100%', height, borderRadius: 12, overflow: 'hidden' }} data-testid="map-picker" />;
}

// A read-only embed for showing a saved location on the business detail page.
// Uses the free Google Maps iframe embed (no API key required).
export function MapEmbed({ latitude, longitude, label, height = 260 }) {
  if (!latitude || !longitude) return null;
  const src = `https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`;
  return (
    <div className="ap-card p-0 overflow-hidden" data-testid="map-embed">
      <iframe
        title={label || 'Location'}
        src={src}
        width="100%"
        height={height}
        loading="lazy"
        style={{ border: 0, display: 'block' }}
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}

export default MapPicker;
